import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { UiButtonComponent } from '../../atoms/button/button.component';
import { UiInputComponent } from '../../atoms/input/input.component';
import { UiMessageComponent } from '../../atoms/message/message.component';
import {
  UiRecaptchaComponent,
  UiRecaptchaSize,
  UiRecaptchaTheme,
} from '../../atoms/recaptcha/recaptcha.component';

export interface UiAuthLoginSubmitEvent {
  username: string;
  password: string;
  /** Token de reCAPTCHA v2; siempre presente en `ui-auth-login`. */
  recaptchaToken?: string;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Template: pantalla de login central para ERP / CRM / BI / ADMIN.
 *
 * Encapsula el formulario completo (logo, título, inputs, reCAPTCHA, botones).
 * No realiza peticiones HTTP; emite `submitted` con las credenciales para
 * que la app consumidora gestione la autenticación.
 *
 * reCAPTCHA es obligatorio: Ingresar solo se habilita con usuario, contraseña
 * y casilla validada. Tras un error de login, llamar a `resetCaptcha()`.
 *
 * @example
 * ```html
 * <ui-auth-login
 *   #authLogin
 *   [recaptchaSiteKey]="recaptchaSiteKey"
 *   (submitted)="onLogin($event)"
 *   (forgotPassword)="onForgot()"
 * />
 * ```
 */
@Component({
  selector: 'ui-auth-login',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    UiInputComponent,
    UiButtonComponent,
    UiMessageComponent,
    UiRecaptchaComponent,
  ],
  templateUrl: './auth-login.component.html',
  styleUrl: './auth-login.component.scss',
})
export class UiAuthLoginComponent {
  /** Ruta de la imagen del logo. */
  readonly logoSrc = input('/images/logo_azul.png');

  /** Texto alternativo del logo para accesibilidad. */
  readonly logoAlt = input('Diseño y Gestión');

  /** Site key pública de reCAPTCHA v2 (obligatoria). */
  readonly recaptchaSiteKey = input.required<string>();

  /** Tema del widget de reCAPTCHA. */
  readonly recaptchaTheme = input<UiRecaptchaTheme>('light');

  /** Tamaño del widget de reCAPTCHA. */
  readonly recaptchaSize = input<UiRecaptchaSize>('normal');

  /** Idioma del widget de reCAPTCHA. */
  readonly recaptchaLanguage = input('es');

  /** Emite las credenciales cuando el formulario es válido y se envía. */
  readonly submitted = output<UiAuthLoginSubmitEvent>();

  /** Emite cuando el usuario pulsa "Recuperar contraseña". */
  readonly forgotPassword = output<void>();

  protected readonly captcha = viewChild(UiRecaptchaComponent);

  protected readonly username = signal('');
  protected readonly password = signal('');
  protected readonly submittedOnce = signal(false);

  protected readonly captchaToken = signal('');
  protected readonly captchaStatus = signal<'idle' | 'expired' | 'error'>(
    'idle',
  );

  protected readonly usernameInvalid = computed(
    () => this.submittedOnce() && !this.isEmailValid(),
  );

  protected readonly passwordInvalid = computed(
    () =>
      this.submittedOnce() &&
      (this.password().length < 6 || this.password().length > 200),
  );

  protected readonly usernameMessage = computed(() =>
    this.username().trim()
      ? 'Ingresa un correo electrónico válido'
      : 'El correo es obligatorio',
  );

  protected readonly passwordMessage = computed(() =>
    this.password()
      ? 'La contraseña debe tener entre 6 y 200 caracteres'
      : 'La contraseña es obligatoria',
  );

  protected readonly captchaInvalid = computed(
    () => this.submittedOnce() && !this.captchaToken(),
  );

  protected readonly captchaMessage = computed(() => {
    const status = this.captchaStatus();
    if (status === 'expired') {
      return 'La verificación expiró. Marca de nuevo la casilla.';
    }
    if (status === 'error') {
      return 'Ocurrió un error con la verificación. Intenta de nuevo.';
    }
    if (this.captchaInvalid()) {
      return 'Confirma que no eres un robot para continuar.';
    }
    return '';
  });

  protected readonly canSubmit = computed(
    () =>
      this.isEmailValid() &&
      this.password().length >= 6 &&
      this.password().length <= 200 &&
      !!this.captchaToken(),
  );

  /**
   * Resetea el widget de reCAPTCHA. Llamar tras un error de autenticación
   * para que el usuario tenga que resolverlo de nuevo.
   */
  resetCaptcha(): void {
    this.captchaToken.set('');
    this.captchaStatus.set('idle');
    this.captcha()?.reset();
  }

  protected onCaptchaResolved(token: string): void {
    this.captchaToken.set(token);
    this.captchaStatus.set('idle');
  }

  protected onCaptchaExpired(): void {
    this.captchaToken.set('');
    this.captchaStatus.set('expired');
  }

  protected onCaptchaError(): void {
    this.captchaToken.set('');
    this.captchaStatus.set('error');
  }

  protected onSubmit(): void {
    this.submittedOnce.set(true);
    if (!this.canSubmit()) return;
    this.submitted.emit({
      username: this.username(),
      password: this.password(),
      recaptchaToken: this.captchaToken(),
    });
  }

  private isEmailValid(): boolean {
    const email = this.username().trim();
    return email.length <= 365 && EMAIL_PATTERN.test(email);
  }
}
